import React from 'react';
import { Box, Tabs, Tab, Fade, IconButton } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';

const StatusChip = styled('span')(({ status, theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(0.5, 1),
  borderRadius: '12px',
  color: 'white',
  ...(status === 'Pendente' && {
    background: theme.palette.mode === 'light' ? 'rgba(255, 165, 0, 0.3)' : 'rgba(255, 165, 0, 0.5)',
  }),
  ...(status === 'Aprovado' && {
    background: theme.palette.success.main,
  }),
  ...(status === 'Rejeitado' && {
    background: theme.palette.error.main,
  }),
  ...(status === 'EmRevisao' && {
    background: theme.palette.info.main,
  }),
}));

const PulseBox = styled(Box)(({ theme, diffInMinutes }) => {
  const getColor = () => {
    if (diffInMinutes < 5) return theme.palette.success.main;
    if (diffInMinutes < 10) return theme.palette.warning.main;
    return theme.palette.error.main;
  };
  return {
    width: 50, // Largura do retângulo
    height: 30, // Altura do retângulo
    borderRadius: 4, // Bordas levemente arredondadas (use 0 para cantos retos)
    border: `2px solid ${getColor()}`, // Borda com a mesma cor do fundo
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    animation: 'pulseAnimation 2s infinite',
    boxShadow: `0 0 0 0 ${getColor()}`, // Sombra com a mesma cor do fundo
    color: 'white',
    fontSize: '14px',
    backgroundColor: getColor(),
    '@keyframes pulseAnimation': {
      '0%': {
        boxShadow: `0 0 0 0px ${getColor()}`,
      },
      '100%': {
        boxShadow: `0 0 0 10px rgba(0, 0, 0, 0)`,
      },
    },
  };
});

const RecordsTable = ({
  tableData,
  onOpenDialog,
  onOpenHistoryDialog,
  tabValue,
  setTabValue,
}) => {
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const columns = [
    { field: 'ordemServico', headerName: 'ID Registro', width: 150 },
    { field: 'equipe', headerName: 'Equipe', width: 150 },
    { field: 'centroDeServico', headerName: 'Centro de Serviço', width: 200 },
    {
      field: 'dataCadastro',
      headerName: 'Data Criação',
      width: 180,
      renderCell: (params) => new Date(params.value).toLocaleString('pt-BR').replace(',', ''),
    },
    ...(tabValue === 1
      ? [
          {
            field: 'analisadoEm',
            headerName: 'Data Aprovação',
            width: 180,
            renderCell: (params) => new Date(params.value).toLocaleString('pt-BR').replace(',', ''),
          },
          {
            field: 'status',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => <StatusChip status={params.value}>{params.value}</StatusChip>,
          },
          { field: 'cadastradoPor', headerName: 'Aprovador', width: 150 },
          {
            field: 'tma',
            headerName: 'TMA',
            width: 150,
            renderCell: (params) => {
              const dataAprovacao = new Date(params.row.analisadoEm);
              const dataCriacao = new Date(params.row.dataCadastro);
              const diffInMinutes = Math.round((dataAprovacao - dataCriacao) / (1000 * 60));
              return diffInMinutes;
            },
          },
        ]
      : []),
    ...(tabValue === 0
      ? [
          {
            field: 'tempoPercorrido',
            headerName: 'Tempo Percorrido (min)',
            width: 120,
            renderCell: (params) => {
              const dataAtual = new Date();
              const dataCriacao = new Date(params.row.dataCadastro);
              const diffInMinutes = Math.round((dataAtual - dataCriacao) / (1000 * 60));
              return (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                  }}
                >
                  <PulseBox diffInMinutes={diffInMinutes} aria-label={`Tempo percorrido: ${diffInMinutes} minutos`}>
                    {diffInMinutes}
                  </PulseBox>
                </Box>
              );
            },
          },
        ]
      : []),
    {
      field: 'acoes',
      headerName: 'Ações',
      width: 150,
      renderCell: (params) => (
        <IconButton
          onClick={() =>
            tabValue === 0
              ? onOpenDialog(params.row.correlationId, params.row.registroIdentificador)
              : onOpenHistoryDialog(params.row.correlationId, params.row.registroIdentificador)
          }
        >
          {tabValue === 0 ? <VisibilityIcon /> : <HistoryIcon />}
        </IconButton>
      ),
    },
  ];

  const rows = (tabValue === 0 ? tableData.filter(record => record.status === 'Pendente') : tableData.filter(record => record.status !== 'Pendente')).map((record) => ({
    id: record.registroIdentificador,
    ordemServico: record.ordemServico,
    equipe: record.equipe,
    centroDeServico: record.centroDeServico,
    status: record.status,
    dataCadastro: record.dataCadastro,
    analisadoEm: record.analisadoEm,
    cadastradoPor: record.cadastradoPor,
    correlationId: record.correlationId,
    registroIdentificador: record.registroIdentificador,
  }));

  return (
    <Box>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          mb: 3,
          '& .MuiTabs-indicator': {
            background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
          },
        }}
      >
        <Tab
          label="Pendentes"
          sx={{
            color: 'text.secondary',
            '&.Mui-selected': {
              color: 'primary.main',
            },
          }}
        />
        <Tab
          label="Histórico"
          sx={{
            color: 'text.secondary',
            '&.Mui-selected': {
              color: 'primary.main',
            },
          }}
        />
      </Tabs>

      <Fade in timeout={500}>
        <div style={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            initialState={{
              sorting: {
                sortModel: [
                  {
                    field: 'dataCadastro',
                    sort: 'desc',
                  },
                ],
              },
            }}
          />
        </div>
      </Fade>
    </Box>
  );
};

export default RecordsTable;